import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTCoins } from '../../context/TCoinsContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import { hasRole, ROLES } from '../../utils/roleManager';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase/firebaseConfig';
import { VideoCallIcon } from './ProfileMenuDropdown';
import LanguageSwitcher from '../LanguageSwitcher';
import LogoutIcon from './LogoutIcon';
import { getFavoritesCount } from '../../utils/favoritesUtils';
import { PACKAGE_TYPES, PACKAGES } from '../../data/packageModels';
import LevelBadge from '../Marketplace/LevelBadge';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useStoreStats } from '../../hooks/useStoreStats';

// MobileFavoritesCountBadge Component
const MobileFavoritesCountBadge = () => {
  const { currentUser } = useAuth();
  const [favoritesCount, setFavoritesCount] = useState(0);

  useEffect(() => {
    if (currentUser) {
      const updateCount = () => {
        const count = getFavoritesCount(currentUser.uid);
        setFavoritesCount(count);
      };
      
      updateCount();
      
      // Listen for favorites updates
      window.addEventListener('favoritesUpdated', updateCount);
      
      return () => {
        window.removeEventListener('favoritesUpdated', updateCount);
      };
    }
  }, [currentUser]);

  if (!currentUser || favoritesCount === 0) return null;

  return (
    <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
      {favoritesCount > 9 ? '9+' : favoritesCount}
    </span>
  );
};

const MobileHamburgerMenu = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { getTotalTCoins } = useTCoins();
  const { getStoreSubscription } = useMarketplace();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const [storePackage, setStorePackage] = useState(null);
  const [storeLevel, setStoreLevel] = useState(null);
  const [storeScore, setStoreScore] = useState(null);
  const [hasDailyBonus, setHasDailyBonus] = useState(false);
  const storeStats = useStoreStats();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
      // ซ่อน bottom navigation เมื่อเปิด menu
      const bottomNav = document.querySelector('nav[class*="fixed bottom-0"]');
      if (bottomNav) {
        bottomNav.style.display = 'none';
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
      // แสดง bottom navigation เมื่อปิด menu
      const bottomNav = document.querySelector('nav[class*="fixed bottom-0"]');
      if (bottomNav) {
        bottomNav.style.display = '';
      }
    };
  }, [isOpen, onClose]);

  const handleMenuClick = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    await signOut(auth);
    onClose();
  };

  // Check daily bonus availability
  useEffect(() => {
    const checkDailyBonus = async () => {
      if (!currentUser) {
        setHasDailyBonus(false);
        return;
      }
      
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const streakData = userData.dailyBonusStreak || {};
          const lastClaim = streakData.lastClaimDate?.toDate?.() || 
                          (streakData.lastClaimDate instanceof Date ? streakData.lastClaimDate : null);
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          // ตรวจสอบว่า claim วันนี้แล้วหรือยัง
          const claimedToday = lastClaim && 
            lastClaim.getDate() === today.getDate() &&
            lastClaim.getMonth() === today.getMonth() &&
            lastClaim.getFullYear() === today.getFullYear();
          
          setHasDailyBonus(!claimedToday);
        }
      } catch (error) {
        console.error('Error checking daily bonus:', error);
        setHasDailyBonus(false);
      }
    };
    
    if (isOpen) {
      checkDailyBonus();
    }
  }, [currentUser, isOpen]);

  // Load store package and level
  useEffect(() => {
    const loadStoreInfo = async () => {
      if (!currentUser?.uid) {
        setStorePackage(null);
        setStoreLevel(null);
        setStoreScore(null);
        return;
      }

      try {
        // Get storeId from users collection
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../../firebase/firebaseConfig');
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          setStorePackage(null);
          setStoreLevel(null);
          setStoreScore(null);
          return;
        }

        const userData = userSnap.data();
        const storeId = userData?.storeId;

        if (!storeId) {
          setStorePackage(null);
          setStoreLevel(null);
          setStoreScore(null);
          return;
        }

        // Get store subscription/package
        const subscription = await getStoreSubscription(storeId);
        const packageType = subscription?.packageType || PACKAGE_TYPES.FREE;
        setStorePackage(packageType);

        // Get store data for level
        const storeRef = doc(db, 'stores', storeId);
        const storeSnap = await getDoc(storeRef);
        
        if (storeSnap.exists()) {
          const storeData = storeSnap.data();
          if (storeData.reputationLevel) {
            setStoreLevel(storeData.reputationLevel);
            setStoreScore(storeData.reputationScore || null);
          }
        }
      } catch (error) {
        console.error('Error loading store info:', error);
        setStorePackage(null);
        setStoreLevel(null);
        setStoreScore(null);
      }
    };

    if (isOpen && currentUser) {
      loadStoreInfo();
    }
  }, [isOpen, currentUser?.uid, currentUser, getStoreSubscription]);

  const isMarketplaceUser = hasRole(currentUser, ROLES.USER) || hasRole(currentUser, ROLES.FARM);

  // Get package icon
  const getPackageIcon = (packageType) => {
    const icons = {
      [PACKAGE_TYPES.FREE]: '🆓',
      [PACKAGE_TYPES.BASIC]: '⭐',
      [PACKAGE_TYPES.PREMIUM]: '💎',
      [PACKAGE_TYPES.ENTERPRISE]: '👑'
    };
    return icons[packageType] || '🆓';
  };

  // Get package name
  const getPackageName = (packageType) => {
    return PACKAGES[packageType]?.name || 'แพ็กเกจฟรี';
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] md:hidden" onClick={onClose} />
      
      {/* Menu */}
      <div 
        ref={menuRef}
        className="fixed inset-y-0 right-0 w-80 bg-white z-[70] md:hidden shadow-xl overflow-y-auto pb-20"
      >
        {/* Header - Green theme to match marketplace with strong contrast */}
        <div className="p-4 text-white mobile-menu-header-green">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold">{t('common.menu')}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* User Info */}
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full bg-white bg-opacity-30 flex items-center justify-center text-white font-bold text-lg">
              {currentUser?.displayName?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold truncate">{currentUser?.displayName || 'User'}</h3>
              <p className="text-sm text-white text-opacity-80 truncate">{currentUser?.email}</p>
            </div>
          </div>
          
          {/* T Coins - Clickable Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Trigger TCoinsWallet modal from parent
              window.dispatchEvent(new CustomEvent('openTCoinsWallet'));
              onClose(); // Close hamburger menu when opening wallet
            }}
            className="relative mt-3 w-full flex items-center space-x-2 text-yellow-300 hover:text-yellow-200 hover:bg-white hover:bg-opacity-10 rounded-lg p-2 transition-colors cursor-pointer"
          >
            <span className="text-xl">🪙</span>
            <span className="font-bold text-lg">{getTotalTCoins().toLocaleString()}</span>
            <span className="text-sm text-white text-opacity-80">T Coins</span>
            {hasDailyBonus && (
              <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold animate-pulse">
                🎁
              </span>
            )}
          </button>
        </div>

        {/* Menu Items */}
        <div className="py-2">
          {/* Marketplace Section */}
          {isMarketplaceUser && (
            <>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t('nav.marketplace')}
              </div>
              
              <button
                onClick={() => handleMenuClick('/marketplace/my-store')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">🏪</span>
                <span className="flex-1">{t('nav.myStore')}</span>
                <div className="flex items-center gap-2">
                  {storePackage && storePackage !== PACKAGE_TYPES.FREE && (
                    <span className="text-xs" title={getPackageName(storePackage)}>
                      {getPackageIcon(storePackage)}
                    </span>
                  )}
                  {storeLevel && (
                    <div className="scale-75">
                      <LevelBadge level={storeLevel} score={storeScore} type="store" />
                    </div>
                  )}
                </div>
              </button>

              <button
                onClick={() => handleMenuClick('/marketplace/favorites')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors relative"
              >
                <span className="text-xl">❤️</span>
                <span className="flex-1">{t('marketplace.favoriteProducts', { defaultValue: 'สินค้าโปรด' })}</span>
                <MobileFavoritesCountBadge />
              </button>

              <button
                onClick={() => handleMenuClick('/marketplace/orders')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">📦</span>
                <span className="flex-1">{t('nav.orders')}</span>
                {storeStats.orders > 0 && (
                  <span className="bg-blue-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[24px] text-center">
                    {storeStats.orders > 99 ? '99+' : storeStats.orders}
                  </span>
                )}
              </button>

              <button
                onClick={() => handleMenuClick('/marketplace/my-store?tab=auctions')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">🔨</span>
                <span className="flex-1">{t('nav.auction')}</span>
                {storeStats.auctions > 0 && (
                  <span className="bg-purple-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[24px] text-center">
                    {storeStats.auctions > 99 ? '99+' : storeStats.auctions}
                  </span>
                )}
              </button>

              <button
                onClick={() => handleMenuClick('/marketplace/my-store?tab=buyerRequests')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">📢</span>
                <span className="flex-1">{t('nav.buyerRequest')}</span>
                {storeStats.buyerRequests > 0 && (
                  <span className="bg-pink-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[24px] text-center">
                    {storeStats.buyerRequests > 99 ? '99+' : storeStats.buyerRequests}
                  </span>
                )}
              </button>

              <button
                onClick={() => handleMenuClick('/marketplace/my-store?tab=chat')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">💬</span>
                <span className="flex-1">แชท</span>
                {storeStats.chats > 0 && (
                  <span className="bg-green-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[24px] text-center">
                    {storeStats.chats > 99 ? '99+' : storeStats.chats}
                  </span>
                )}
              </button>

              <button
                onClick={() => handleMenuClick('/marketplace/my-video-calls')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <VideoCallIcon className="w-5 h-5 text-purple-600" />
                <span className="flex-1">{t('nav.videoCalls')}</span>
                {storeStats.videoCalls > 0 && (
                  <span className="bg-purple-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[24px] text-center">
                    {storeStats.videoCalls > 99 ? '99+' : storeStats.videoCalls}
                  </span>
                )}
              </button>

              {hasRole(currentUser, ROLES.FARM) && (
                <button
                  onClick={() => handleMenuClick('/marketplace/farm-dashboard')}
                  className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
                >
                  <span className="text-xl">🌾</span>
                  <span className="flex-1">{t('nav.farmDashboard')}</span>
                </button>
              )}
            </>
          )}

          {/* Customer Section */}
          {hasRole(currentUser, ROLES.CUSTOMER) && (
            <>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t('nav.services')}
              </div>
              
              <button
                onClick={() => handleMenuClick('/trip-plans')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">🗺️</span>
                <span className="flex-1">{t('nav.tripPlans')}</span>
              </button>

              <button
                onClick={() => handleMenuClick('/bookings')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">📋</span>
                <span className="flex-1">{t('nav.bookingHistory')}</span>
              </button>

              <button
                onClick={() => handleMenuClick('/agoda')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">🏨</span>
                <span className="flex-1">{t('nav.bookHotel')}</span>
              </button>
            </>
          )}

          {/* Momo Section */}
          {hasRole(currentUser, ROLES.MOMO) && (
            <>
              <button
                onClick={() => handleMenuClick('/trip-plans')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">🗺️</span>
                <span className="flex-1">Trip Plans</span>
              </button>

              <button
                onClick={() => handleMenuClick('/momo')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">👨‍💼</span>
                <span className="flex-1">แดชบอร์ดโม</span>
              </button>

              <button
                onClick={() => handleMenuClick('/agoda')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">🏨</span>
                <span className="flex-1">จองที่พัก</span>
              </button>
            </>
          )}

          {/* Girl Section */}
          {hasRole(currentUser, ROLES.GIRL) && (
            <>
              <button
                onClick={() => handleMenuClick('/trip-plans')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">🗺️</span>
                <span className="flex-1">Trip Plans</span>
              </button>

              <button
                onClick={() => handleMenuClick('/girl')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">👩</span>
                <span className="flex-1">แดชบอร์ดน้อง</span>
              </button>

              <button
                onClick={() => handleMenuClick('/agoda')}
                className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl">🏨</span>
                <span className="flex-1">จองที่พัก</span>
              </button>
            </>
          )}

          {/* Admin Section */}
          {hasRole(currentUser, ROLES.ADMIN) && (
            <button
              onClick={() => handleMenuClick('/admin')}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
            >
              <span className="text-xl">👑</span>
              <span className="flex-1">แอดมิน</span>
            </button>
          )}

          {/* Settings Section */}
          <div className="border-t border-gray-200 mt-2 pt-2">
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {t('common.settings')}
            </div>

            <div className="px-4 py-2">
              <LanguageSwitcher />
            </div>

            <Link
              to="/profile/settings"
              onClick={onClose}
              className="block w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
            >
              <span className="text-xl">⚙️</span>
              <span className="flex-1">{t('settings.title')}</span>
            </Link>

            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-red-50 text-red-600 transition-colors"
            >
              <LogoutIcon className="w-5 h-5" />
              <span className="flex-1">{t('settings.logout')}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileHamburgerMenu;

